







































































































































import { Component, Mixins, Prop, Inject } from "vue-property-decorator";
import { validate } from "vee-validate";
import { FunctionalComponentOptions } from "vue";
import _ from "@/boot/lodash";

// Mixins
import Context from "@/mixins/context";

// Enums/Data
import { ProductOrderTypes } from "@/data/enums";

// Types
import { IBasket, IBasketProduct } from "@/models/baskets";
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import {
  IProvisionProviderData,
  IProvisionProviderMethods
} from "@/models/providers/provisioning";
import { IProductConfig } from "@/models/providers/products";

@Component({
  components: {
    "product-image": () =>
      import("@/components/app/global/products/productImage.vue"),
    "basket-item-details": () =>
      import(
        "@/components/app/global/baskets/basket/basketView/basketItemDetails.vue"
      ),
    "basket-item-details-dropdown": () =>
      import(
        "@/components/app/global/baskets/basket/express/basketItems/basketItemDetailsDropdown.vue"
      )
  }
})
export default class OnePageBasketItem extends Mixins(Context) {
  // Inject

  @Inject({ from: "basketProviderData" })
  public readonly $basketData!: () => IBasketProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  @Inject({ from: "provisionProviderData" })
  public readonly $provisionProviderData!: () => IProvisionProviderData;

  @Inject({ from: "provisionProviderMethods" })
  public readonly $provisionMethods!: IProvisionProviderMethods;

  // Props

  @Prop({ required: true })
  public basketProduct!: IBasketProduct;

  // Variables

  public isDeleting = false;
  public isProcessing = false;
  public provisioningErrors: number[] = [];
  public quantity = 1;
  public savedBasketId: IBasket["id"] | null = null;
  public showDetails = true;
  public updateQuantityDebounced = _.debounce(this.updateQuantity, 250);

  // Computed

  public get basketData() {
    return this.$basketData();
  }

  public get provisionData() {
    return this.$provisionProviderData();
  }

  public get basket() {
    return this.basketData.basket;
  }

  public get brandTaxType() {
    return this.basketData.brandTaxType;
  }

  public get canEditItem() {
    if (this.isAdmin) return true;
    if (this.basketProduct.product.trial_supported) return true;
    if (
      this.basketProduct.product.products_options.length > 0 ||
      this.basketProduct.product.products_attributes.length > 0 ||
      (this.provisionData.hasProvisioning &&
        !_.isEmpty(this.basketProductFields))
    ) {
      return true;
    }

    const filteredPrices = _.filter(
      this.basketProduct.product.prices,
      price => {
        return (
          price.currency_id === this.basket.currency_id &&
          price.pricelist_id === this.basket.pricelist_id
        );
      }
    );

    return filteredPrices.length > 1;
  }

  public get editItemControl(): FunctionalComponentOptions {
    const isProcessing = this.isProcessing || this.basketData.isProcessing;
    return {
      functional: true,
      render: (h, { children }) =>
        h(
          "b-tooltip",
          {
            class: "is-block",
            props: {
              label: this.$t("_sentence.basket.item_has_invalid_data"),
              type: "is-warning",
              size: "is-small",
              multilined: true,
              active: !isProcessing && this.hasProvisioningError
            }
          },
          [
            isProcessing
              ? h("a", { class: "is-disabled" }, children)
              : h(
                  "a",
                  {
                    on: {
                      click: () => {
                        this.$emit("edit", this.basketProduct);
                      }
                    }
                  },
                  children
                )
          ]
        )
    };
  }

  public get deleteItemControl(): FunctionalComponentOptions {
    const isProcessing = this.isProcessing || this.basketData.isProcessing;
    return {
      functional: true,
      render: h =>
        h(
          "b-tooltip",
          {
            class: "is-block",
            props: {
              label: this.$t("_action.remove_item"),
              type: "is-danger",
              position: "is-left",
              active: !isProcessing
            }
          },
          [
            h(
              "a",
              isProcessing || this.isDeleting
                ? { class: "is-disabled" }
                : { on: { click: this.deleteProduct } },
              [
                h("b-icon", {
                  class: { "fa-pulse": this.isDeleting },
                  props: {
                    icon: this.isDeleting ? "spinner" : "trash-alt",
                    pack: this.isDeleting ? "fas" : "far"
                  }
                })
              ]
            )
          ]
        )
    };
  }

  public get quantityValidation() {
    return `min_value:${
      this.basketProduct.product.unit_quantity
    }|divisible_by:${this.basketProduct.product.unit_quantity}${
      this.basketProduct.product.max_order_quantity
        ? `|max_value:${this.basketProduct.product.max_order_quantity}`
        : ""
    }`;
  }

  public get hasProvisioningError() {
    return this.productProvisionErrors.includes(this.basketProduct.id);
  }

  public get basketProductFields() {
    return this.$provisionMethods.basketProductFields(
      this.basketData.basketId,
      this.basketProduct.id
    );
  }

  public get nonQuantifiableOptions() {
    return _.filter(
      this.basketProduct.options,
      option => option.product.order_type === ProductOrderTypes.SINGLE_OPTION
    );
  }

  public get isQuantityBased() {
    return (
      this.basketProduct.product.order_type === ProductOrderTypes.QUANTITY_BASED
    );
  }

  public get subProductIds() {
    return _.map(
      _.concat(this.basketProduct.options, this.basketProduct.attributes),
      (item: any) => item.product_id
    );
  }

  public get productProvisionErrors() {
    return _.map(
      _.get(this.provisionData.provisionErrors, "data"),
      (errorValue, errorKey) => {
        const productIndex = errorKey.split(".")[1];
        return _.get(this.basket, `products[${productIndex}].id`, "");
      }
    );
  }

  // Methods

  public getBasketProductFields() {
    return this.$provisionMethods.getBasketProductFields(
      this.basket.id,
      this.basketProduct.id,
      this.subProductIds,
      true
    );
  }

  public getItemName(item = this.basketProduct) {
    return _.compact([
      item?.product?.name_translated || item?.product?.name,
      item?.service_identifier ? `(${item.service_identifier})` : null
    ]).join(" ");
  }

  public getItemCategory(item = this.basketProduct) {
    return item.product.category.name_translated || item.product.category.name;
  }

  public hasDiscount(item = this.basketProduct) {
    return this.brandTaxType
      ? item.configuration_total_amount_formatted !==
          item.configuration_total_discounted_amount_formatted
      : item.configuration_net_amount_formatted !==
          item.configuration_net_amount_discounted_formatted;
  }

  public async updateProduct(callback: () => {}) {
    if (this.isProcessing) return;
    try {
      this.isProcessing = true;
      await callback();
    } catch (error) {
      this.quantity = this.basketProduct.quantity;
      this.$store.dispatch("api/handleError", error);
      if (
        !this.$store.getters["api/is"](error, 422) &&
        !this.$store.getters["api/is"](error, 404)
      ) {
        return;
      }

      this.$store.dispatch("ui/open/windowModal", {
        config: {
          component: () => import("@/components/app-base/UAlert.vue"),
          width: 480,
          canCancel: [],
          props: {
            title: this.$t("_.unexpected_error") as string,
            message: this.$t(
              "_sentence.something_went_wrong_with_your_basket"
            ) as string,
            confirmText: this.$t("_.refresh_basket").toString()
          },
          onCancel: () => this.$basketMethods.getBasket(true),
          events: {
            close: () => this.$basketMethods.getBasket(true)
          }
        }
      });
    } finally {
      this.isProcessing = false;
    }
  }

  public async validateQuantity() {
    const result = await validate(this.quantity, this.quantityValidation);

    return result.valid;
  }

  public async updateQuantity() {
    this.updateProduct(async () => {
      if (!(await this.validateQuantity())) {
        this.quantity = this.basketProduct.quantity;
        throw this.$t("_sentence.quantity_is_not_valid");
      }
      await this.$basketMethods.updateBasketProduct(
        this.basketProduct.id,
        this.computeProductConfig()
      );
    });
  }

  public computeProductConfig() {
    return {
      quantity: this.quantity,
      product_id: this.basketProduct.product_id,
      billing_cycle_months: this.basketProduct.billing_cycle_months,
      options: _.map(this.basketProduct.options, option => ({
        product_id: option.product_id,
        billing_cycle_months: option.billing_cycle_months,
        unit_quantity: option.unit_quantity,
        ...(option.selling_price !== option.base_price
          ? {
              selling_price: option.selling_price
            }
          : null)
      })),
      attributes: _.map(this.basketProduct.attributes, attribute => ({
        product_id: attribute.product_id
      })),
      ...(this.basketProduct.selling_price !== this.basketProduct.base_price
        ? {
            selling_price: this.basketProduct.selling_price
          }
        : null)
    } as IProductConfig;
  }

  public async deleteProduct() {
    if (this.isDeleting) return;
    try {
      this.isDeleting = true;
      await this.updateProduct(async () => {
        await this.$basketMethods.deleteProduct(
          this.basketProduct.id,
          this.basketProduct.invoice_id
        );
        await this.$provisionMethods.checkConfiguration(this.basket.id);
      });
    } finally {
      this.isDeleting = false;
    }
  }

  // Life cycle methods

  public async created() {
    this.quantity = this.basketProduct.quantity;
    this.savedBasketId = this.basket.id;

    try {
      if (this.provisionData.hasProvisioning) {
        await this.getBasketProductFields();
      }
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public beforeDestroy() {
    this.$provisionMethods.destroyBasketProductFields(
      this.savedBasketId as IBasket["id"],
      this.basketProduct.id
    );
  }
}
